import { useCallback, useEffect, useState } from 'react';

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface WebkitDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

/**
 * Fullscreen API のラッパ。
 *
 * 注意: iPhone の Safari は Fullscreen API を **サポートしていない**
 * （iPad は iPadOS 12 以降で対応）。iPhone で全画面にする唯一の方法は
 * 「ホーム画面に追加」した PWA として起動すること。
 * そのため UI 側では isSupported を見て、非対応なら PWA 導線を出す。
 */
export function useFullscreen(target?: React.RefObject<HTMLElement>) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isSupported =
    typeof document !== 'undefined' &&
    (document.fullscreenEnabled ||
      Boolean((document as WebkitDocument).webkitFullscreenElement !== undefined &&
        (document.documentElement as WebkitFullscreenElement).webkitRequestFullscreen));

  useEffect(() => {
    const onChange = () => {
      const doc = document as WebkitDocument;
      setIsFullscreen(Boolean(document.fullscreenElement ?? doc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const enter = useCallback(async () => {
    const el = (target?.current ?? document.documentElement) as WebkitFullscreenElement;
    try {
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch (e) {
      console.warn('[fullscreen] request rejected', e);
    }
  }, [target]);

  const exit = useCallback(async () => {
    const doc = document as WebkitDocument;
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
    } catch {
      /* 既に解除されている場合は無視 */
    }
  }, []);

  const toggle = useCallback(async () => {
    if (isFullscreen) await exit();
    else await enter();
  }, [isFullscreen, enter, exit]);

  return { isFullscreen, isSupported, enter, exit, toggle };
}

/**
 * screen.orientation.lock の引数。
 * TypeScript の DOM 型定義にまだ入っていないので自前で持つ。
 */
export type OrientationLock =
  | 'any'
  | 'natural'
  | 'landscape'
  | 'portrait'
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

/**
 * 画面の向きをロックする。
 * 対応は Android Chrome + 全画面時のみ。iOS は非対応なので失敗を握りつぶす。
 */
export async function lockOrientation(orientation: OrientationLock): Promise<boolean> {
  const so = screen.orientation as ScreenOrientation & { lock?: (o: OrientationLock) => Promise<void> };
  if (!so?.lock) return false;
  try {
    await so.lock(orientation);
    return true;
  } catch {
    return false;
  }
}

/** 縦持ち / 横持ちの判定（リサイズに追従する） */
export function useOrientation(): 'portrait' | 'landscape' {
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(() =>
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
  );

  useEffect(() => {
    const update = () => setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return orientation;
}

/**
 * 画面スリープを防ぐ。プレイ中はこれが無いと数十秒で暗転する。
 * iOS は 16.4 以降で対応。
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        /* バッテリー低下時などは拒否される。プレイは続行できるので無視。 */
      }
    };

    void acquire();

    // タブ復帰時に取り直す（バックグラウンドで自動解放されるため）
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) void acquire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
